import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import http from "../../../../utils/http";
import { errorToastMessage, toastMessage } from "../../../../utils/toast";
import {
  CommonDrawerStyle,
  DrawerForm,
  DrawerHeader,
} from "../../../Common/styles/drawer";
import { ModalDrawerButtonStyles } from "../../../Common/styles/modal";
import {
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
} from "../../../Common/styles/table";
import { debounce } from "lodash";
import { DateTime } from "luxon";
import { useAppSelector } from "../../../../Redux/hooks";
import { PARTICIPANT_LOCKED_MESSAGE } from "../../../../utils/participantLock";

type Props = {
  closeDrawer: () => void;
  refreshPage: () => void;
  formType: "consent" | "sunshine";
};

const SendConsentDrawer: React.FC<Props> = ({
  closeDrawer,
  refreshPage,
  formType,
}) => {
  const { id: studyId, participantId } = useParams();
  const isLocked = useAppSelector((state) => state.participantTab.isLocked);

  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [consents, setConsents] = useState<any[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [selectedConsents, setSelectedConsents] = useState<any[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const endDate = DateTime.now().toUTC().toISO();
        let url = `/admin/study/${studyId}/econsent`;
        if (formType === "sunshine") {
          url = `/admin/study/${studyId}/sunshine`;
        }
        url += `?status=active&endDate=${endDate}&page=1&size=20`;
        if (searchText) {
          url += `&q=${searchText}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data?.data;
        const newData = data?.map((item: any) => ({
          id: item?.id,
          name: item?.name,
          version: item?.latestVersion?.version,
          versionId: item?.latestVersion?.id,
        }));

        setConsents(newData || []);

        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchData();
  }, [studyId, searchText, formType]);

  async function submitHandler() {
    if (isLocked) {
      toastMessage("error", PARTICIPANT_LOCKED_MESSAGE);
      return;
    }
    try {
      if (selectedConsents.length > 0) {
        setSubmitLoader(true);
        let body = {
          versionIds: [...selectedConsents.map((c) => c.versionId)],
        };
        let prefix = `/admin/study/${studyId}/econsent`;
        if (formType === "sunshine") {
          prefix = `/admin/study/${studyId}/sunshine`;
        }
        let res: AxiosResponse;
        res = await http.post(
          `${prefix}/participants/${participantId}/send`,
          body
        );

        toastMessage("success", res.data.message);
        closeDrawer();
        refreshPage();
        setSubmitLoader(false);
      } else {
        toastMessage("warning", "Select atleast one consent to send");
      }
    } catch (err) {
      errorToastMessage(err as Error);
      setSubmitLoader(false);
    }
  }

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        setSearchText(val);
      }, 500),
    [setSearchText]
  );

  return (
    <Box sx={{ ...CommonDrawerStyle, height: "calc(100vh - 200px)" }}>
      <DrawerHeader
        closeDrawer={closeDrawer}
        title={`Send ${formType === "consent" ? "Consent" : "Sunshine Form"}`}
      />
      <Box
        sx={{
          height: "64px",
          display: "flex",
          alignItems: "center",
          mb: 2,
          width: "50%",
        }}
      >
        <TextField
          placeholder={`Search for ${
            formType === "consent" ? "Consents" : "Forms"
          }`}
          style={{ width: "100%" }}
          defaultValue={searchText}
          onChange={(e) => modifySearchTerm(e.target.value)}
        />
      </Box>

      <Box style={{ height: "100%" }}>
        <>
          <Box sx={{ ...DrawerForm }}>
            <Box sx={{ height: "80%", overflow: "auto" }}>
              <Table
                sx={{
                  ...TableBorderRadiusTopLeftRight,
                }}
              >
                <TableHead>
                  <TableRow>
                    <StyledTableCell>
                      <FormControlLabel
                        label={
                          <Typography fontWeight={"medium"}>
                            {formType === "consent"
                              ? "Consent Name"
                              : "Form Name"}
                          </Typography>
                        }
                        control={
                          <Checkbox
                            checked={false}
                            onChange={(event) => {
                              if (event.target.checked) {
                                const newSelected = [...selectedConsents];
                                consents.forEach((c) => {
                                  const found = newSelected.find(
                                    (s) => s.versionId === c.versionId
                                  );
                                  if (!found) {
                                    newSelected.push({
                                      versionId: c.versionId,
                                      name: c.name + " " + c.version,
                                    });
                                  }
                                });
                                setSelectedConsents(newSelected);
                              }
                            }}
                          />
                        }
                      />
                    </StyledTableCell>
                    <StyledTableCell>Version</StyledTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      <CircularProgress size={25} />
                    </Box>
                  ) : consents.length > 0 ? (
                    consents.map((consent) => {
                      const checked = !!selectedConsents.find(
                        (c) => c.versionId === consent.versionId
                      );
                      return (
                        <TableRow key={consent.versionId}>
                          <StyledTableCell>
                            <FormControlLabel
                              label={
                                <Typography fontWeight={"regular"}>
                                  {consent.name}
                                </Typography>
                              }
                              control={
                                <Checkbox
                                  checked={checked}
                                  onChange={(event) => {
                                    let newSelected = [...selectedConsents];
                                    if (event.target.checked) {
                                      newSelected.push({
                                        versionId: consent.versionId,
                                        name:
                                          consent.name + " " + consent.version,
                                      });
                                    } else {
                                      newSelected = newSelected.filter(
                                        (c) => c.versionId !== consent.versionId
                                      );
                                    }
                                    setSelectedConsents(newSelected);
                                  }}
                                />
                              }
                            />
                          </StyledTableCell>

                          <StyledTableCell>{consent.version}</StyledTableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <Typography textAlign="center" sx={{ mt: 2 }}>
                      No {formType === "consent" ? "consents" : "forms"}{" "}
                      available
                    </Typography>
                  )}
                </TableBody>
              </Table>
            </Box>

            {selectedConsents.length > 0 && (
              <Box
                sx={{
                  marginTop: 2,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {selectedConsents.map((consent) => {
                  return (
                    <Chip
                      key={consent.versionId}
                      label={consent.name}
                      onDelete={() => {
                        const newSelected = selectedConsents.filter(
                          (c) => c.versionId !== consent.versionId
                        );
                        setSelectedConsents(newSelected);
                      }}
                    />
                  );
                })}
              </Box>
            )}
          </Box>

          <Box sx={{ mt: "auto" }}>
            <Divider />
            <Box sx={ModalDrawerButtonStyles}>
              {!submitLoader ? (
                <>
                  <Button onClick={closeDrawer} variant="outlined">
                    Cancel
                  </Button>
                  <Button
                    onClick={submitHandler}
                    variant="contained"
                    disabled={isLocked}
                    title={isLocked ? PARTICIPANT_LOCKED_MESSAGE : ""}
                  >
                    Send {formType === "consent" ? "Consent" : "Form"}
                  </Button>
                </>
              ) : (
                <CircularProgress size={25} />
              )}
            </Box>
          </Box>
        </>
      </Box>
    </Box>
  );
};

export default SendConsentDrawer;
